import { ThrowLiteError } from '../lite-error.js';
import { bumpVisibilityEpoch } from '../engine/engine.js';
import { retireGpuResources } from '../engine/gpu-resource-retirement.js';
import { release } from '../resource/ref-count.js';
import { initMeshTransform, uploadMeshToGPU } from './mesh.js';
import { computeAabb } from '../math/compute-aabb.js';
import { createSphereData } from './create-sphere.js';
import { createBoxData } from './create-box.js';
import { createTorusData } from './create-torus.js';
import { createTorusKnotData } from './create-torus-knot.js';
import { createFlatGroundData, createGroundFromHeightMap as createGroundFromHeightMap$1 } from './create-ground.js';
import { createCylinderData } from './create-cylinder.js';
import { createCapsuleData } from './create-capsule.js';
import { createPlaneData } from './create-plane.js';
import { createDiscData } from './create-disc.js';
import { createPolyhedronData } from './create-polyhedron.js';
import { createRibbonData } from './create-ribbon.js';
import { createTubeData } from './create-tube.js';
import { createExtrudeShapeData } from './create-extrude.js';
import { _markWorldMatrixDirty } from '../scene/world-matrix-state.js';

function retainMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors) {
  const [min, max] = computeAabb(positions);
  mesh.boundMin = isFinite(min[0]) ? min : void 0;
  mesh.boundMax = isFinite(max[0]) ? max : void 0;
  mesh._cpuPositions = positions;
  mesh._cpuNormals = normals;
  mesh._cpuUvs = uvs?.length ? uvs : void 0;
  mesh._cpuUv2s = uvs2?.length ? uvs2 : null;
  mesh._cpuTangents = tangents?.length ? tangents : null;
  mesh._cpuColors = colors?.length ? colors : null;
  mesh._cpuIndices = indices;
  mesh._cpuGpuIndices = indices;
  mesh._cpuIndexFormat = "uint32";
  engine._dlr?.m(mesh, mesh._cpuUv2s, mesh._cpuTangents, mesh._cpuColors, indices, "uint32");
}
function validateCapacityGeometry(mesh, positions, normals, indices, uvs, uvs2, tangents, colors) {
  const gpu = mesh._gpu;
  const vertexCount = positions.length / 3;
  if (gpu._vbLayout || (gpu._refCount ?? 1) > 1) {
    ThrowLiteError(357);
  }
  if (!Number.isInteger(vertexCount) || normals.length !== positions.length || indices.length % 3 !== 0 || gpu.indexFormat !== "uint32") {
    ThrowLiteError(358);
  }
  const hasUvs = !!uvs && uvs.length > 0;
  const hasUv2s = !!uvs2 && uvs2.length > 0;
  const hasTangents = !!tangents && tangents.length > 0;
  const hasColors = !!colors && colors.length > 0;
  if (hasUvs !== !!gpu.hasUv || hasUvs && uvs.length !== vertexCount * 2 || hasUv2s !== !!gpu.hasUv2 || hasUv2s && uvs2.length !== vertexCount * 2 || hasTangents !== !!gpu.hasTangent || hasTangents && tangents.length !== vertexCount * 4 || hasColors !== !!gpu.hasColor || hasColors && colors.length !== vertexCount * 4) {
    ThrowLiteError(359);
  }
  return vertexCount;
}
function createMeshFromData(engine, name, positions, normals, indices, uvs, uvs2, tangents, colors) {
  const [min, max] = computeAabb(positions);
  const mesh = initMeshTransform({
    name,
    material: null,
    receiveShadows: false,
    boundMin: isFinite(min[0]) ? min : void 0,
    boundMax: isFinite(max[0]) ? max : void 0,
    _gpu: uploadMeshToGPU(engine, positions, normals, indices, uvs, uvs2, tangents, colors)
  });
  mesh._cpuPositions = positions;
  mesh._cpuNormals = normals;
  mesh._cpuUvs = uvs;
  mesh._cpuIndices = indices;
  engine._dlr?.m(mesh, uvs2 ?? null, tangents ?? null, colors ?? null, indices, "uint32");
  return mesh;
}
function invalidateRenderBundles(engine) {
  bumpVisibilityEpoch();
  for (const ctx of engine._renderingContexts) {
    const sc = ctx;
    if (sc._renderableVersion !== void 0) {
      sc._renderableVersion++;
    }
  }
}
function updateMeshPositions(engine, mesh, positions, vertexOffset = 0, vertexCount, sourceVertexOffset = 0) {
  writeVertexAttributeRange(engine, mesh, mesh._gpu.positionBuffer, positions, 3, vertexOffset, vertexCount, sourceVertexOffset);
}
function writeVertexAttributeRange(engine, mesh, buffer, values, components, vertexOffset, vertexCount, sourceVertexOffset) {
  if ((mesh._gpu._refCount ?? 1) > 1) {
    ThrowLiteError(360, mesh.name);
  }
  const sourceVertexCount = values.length / components;
  const count = vertexCount ?? sourceVertexCount - sourceVertexOffset;
  if (!Number.isInteger(sourceVertexCount) || !Number.isInteger(vertexOffset) || vertexOffset < 0 || !Number.isInteger(sourceVertexOffset) || sourceVertexOffset < 0 || !Number.isInteger(count) || count < 0 || sourceVertexOffset + count > sourceVertexCount) {
    ThrowLiteError(361);
  }
  if (count === 0) {
    return;
  }
  const bytesPerVertex = components * 4;
  const destinationByteOffset = vertexOffset * bytesPerVertex;
  const byteLength = count * bytesPerVertex;
  if (destinationByteOffset + byteLength > buffer.size) {
    ThrowLiteError(362);
  }
  engine._device.queue.writeBuffer(buffer, destinationByteOffset, values.buffer, values.byteOffset + sourceVertexOffset * bytesPerVertex, byteLength);
  _markWorldMatrixDirty(mesh);
}
function updateMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors) {
  const gpu = mesh._gpu;
  const vertexCount = positions.length / 3;
  const hasUvs = !!uvs && uvs.length > 0;
  const hasUv2s = !!uvs2 && uvs2.length > 0;
  const hasTangents = !!tangents && tangents.length > 0;
  const hasColors = !!colors && colors.length > 0;
  if (gpu._vbLayout || (gpu._refCount ?? 1) > 1) {
    ThrowLiteError(363);
  }
  if (!Number.isInteger(vertexCount) || mesh._cpuPositions?.length !== positions.length || normals.length !== positions.length || mesh._cpuIndices?.length !== indices.length || indices.length > gpu.indexCount || gpu.indexFormat !== "uint32") {
    ThrowLiteError(364);
  }
  if (hasUvs !== !!gpu.hasUv || hasUvs && uvs.length !== vertexCount * 2 || hasUv2s !== !!gpu.hasUv2 || hasUv2s && uvs2.length !== vertexCount * 2 || hasTangents !== !!gpu.hasTangent || hasTangents && tangents.length !== vertexCount * 4 || hasColors !== !!gpu.hasColor || hasColors && colors.length !== vertexCount * 4) {
    ThrowLiteError(365);
  }
  const queue = engine._device.queue;
  queue.writeBuffer(gpu.positionBuffer, 0, positions.buffer, positions.byteOffset, positions.byteLength);
  queue.writeBuffer(gpu.normalBuffer, 0, normals.buffer, normals.byteOffset, normals.byteLength);
  queue.writeBuffer(gpu.indexBuffer, 0, indices.buffer, indices.byteOffset, indices.byteLength);
  if (hasUvs) {
    queue.writeBuffer(gpu.uvBuffer, 0, uvs.buffer, uvs.byteOffset, uvs.byteLength);
  }
  if (hasUv2s) {
    queue.writeBuffer(gpu.uv2Buffer, 0, uvs2.buffer, uvs2.byteOffset, uvs2.byteLength);
  }
  if (hasTangents) {
    queue.writeBuffer(gpu.tangentBuffer, 0, tangents.buffer, tangents.byteOffset, tangents.byteLength);
  }
  if (hasColors) {
    queue.writeBuffer(gpu.colorBuffer, 0, colors.buffer, colors.byteOffset, colors.byteLength);
  }
  retainMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors);
  _markWorldMatrixDirty(mesh);
}
function updateMeshGeometryCapacity(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors, reserveFactor = 1.25) {
  if (!Number.isFinite(reserveFactor) || reserveFactor < 1) {
    ThrowLiteError(366);
  }
  const vertexCount = validateCapacityGeometry(mesh, positions, normals, indices, uvs, uvs2, tangents, colors);
  const oldGpu = mesh._gpu;
  const vertexCapacity = oldGpu._vertexCapacity ?? (mesh._cpuPositions ? mesh._cpuPositions.length / 3 : vertexCount);
  const indexCapacity = oldGpu._indexCapacity ?? oldGpu.indexCount;
  if (vertexCount > vertexCapacity || indices.length > indexCapacity) {
    const nextVertexCapacity = vertexCount > vertexCapacity ? Math.ceil(vertexCount * reserveFactor) : vertexCapacity;
    const requestedIndexCapacity = indices.length > indexCapacity ? Math.ceil(indices.length * reserveFactor) : indexCapacity;
    const nextIndexCapacity = Math.ceil(requestedIndexCapacity / 3) * 3;
    const paddedPositions = new Float32Array(nextVertexCapacity * 3);
    const paddedNormals = new Float32Array(nextVertexCapacity * 3);
    const paddedIndices2 = new Uint32Array(nextIndexCapacity);
    paddedPositions.set(positions);
    paddedNormals.set(normals);
    paddedIndices2.set(indices);
    const paddedUvs = uvs?.length ? new Float32Array(nextVertexCapacity * 2) : void 0;
    const paddedUv2s = uvs2?.length ? new Float32Array(nextVertexCapacity * 2) : void 0;
    const paddedTangents = tangents?.length ? new Float32Array(nextVertexCapacity * 4) : void 0;
    const paddedColors = colors?.length ? new Float32Array(nextVertexCapacity * 4) : void 0;
    paddedUvs?.set(uvs);
    paddedUv2s?.set(uvs2);
    paddedTangents?.set(tangents);
    paddedColors?.set(colors);
    resizeMeshGeometry(engine, mesh, paddedPositions, paddedNormals, paddedIndices2, paddedUvs, paddedUv2s, paddedTangents, paddedColors);
    mesh._gpu._vertexCapacity = nextVertexCapacity;
    mesh._gpu._indexCapacity = nextIndexCapacity;
    mesh._gpu._indexScratch = paddedIndices2;
    retainMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors);
    return { stable: false, vertexCapacity: nextVertexCapacity, indexCapacity: nextIndexCapacity };
  }
  oldGpu._vertexCapacity = vertexCapacity;
  oldGpu._indexCapacity = indexCapacity;
  const paddedIndices = oldGpu._indexScratch?.length === indexCapacity ? oldGpu._indexScratch : oldGpu._indexScratch = new Uint32Array(indexCapacity);
  paddedIndices.fill(0);
  paddedIndices.set(indices);
  const queue = engine._device.queue;
  queue.writeBuffer(oldGpu.positionBuffer, 0, positions.buffer, positions.byteOffset, positions.byteLength);
  queue.writeBuffer(oldGpu.normalBuffer, 0, normals.buffer, normals.byteOffset, normals.byteLength);
  queue.writeBuffer(oldGpu.indexBuffer, 0, paddedIndices.buffer, paddedIndices.byteOffset, paddedIndices.byteLength);
  if (uvs?.length) {
    queue.writeBuffer(oldGpu.uvBuffer, 0, uvs.buffer, uvs.byteOffset, uvs.byteLength);
  }
  if (uvs2?.length) {
    queue.writeBuffer(oldGpu.uv2Buffer, 0, uvs2.buffer, uvs2.byteOffset, uvs2.byteLength);
  }
  if (tangents?.length) {
    queue.writeBuffer(oldGpu.tangentBuffer, 0, tangents.buffer, tangents.byteOffset, tangents.byteLength);
  }
  if (colors?.length) {
    queue.writeBuffer(oldGpu.colorBuffer, 0, colors.buffer, colors.byteOffset, colors.byteLength);
  }
  retainMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors);
  _markWorldMatrixDirty(mesh);
  return { stable: true, vertexCapacity, indexCapacity };
}
function resizeMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors) {
  const old = mesh._gpu;
  invalidateRenderBundles(engine);
  mesh._gpu = uploadMeshToGPU(engine, positions, normals, indices, uvs, uvs2, tangents, colors);
  retainMeshGeometry(engine, mesh, positions, normals, indices, uvs, uvs2, tangents, colors);
  _markWorldMatrixDirty(mesh);
  if (release(old)) {
    retireGpuResources(engine, () => {
      old.positionBuffer.destroy();
      old.normalBuffer.destroy();
      old.indexBuffer.destroy();
      old.uvBuffer.destroy();
      old.uv2Buffer?.destroy();
      old.tangentBuffer?.destroy();
      old.colorBuffer?.destroy();
    });
  }
}
function updateMeshNormals(engine, mesh, normals, vertexOffset = 0, vertexCount, sourceVertexOffset = 0) {
  const gpu = mesh._gpu;
  if (!gpu.normalBuffer) {
    return;
  }
  writeVertexAttributeRange(engine, mesh, gpu.normalBuffer, normals, 3, vertexOffset, vertexCount, sourceVertexOffset);
}
function updateMeshColors(engine, mesh, colors, vertexOffset = 0, vertexCount, sourceVertexOffset = 0) {
  const gpu = mesh._gpu;
  if (!gpu.colorBuffer) {
    return;
  }
  writeVertexAttributeRange(engine, mesh, gpu.colorBuffer, colors, 4, vertexOffset, vertexCount, sourceVertexOffset);
}
function updateMeshUvs(engine, mesh, uvs, vertexOffset = 0, vertexCount, sourceVertexOffset = 0) {
  const gpu = mesh._gpu;
  if (!gpu.uvBuffer) {
    return;
  }
  writeVertexAttributeRange(engine, mesh, gpu.uvBuffer, uvs, 2, vertexOffset, vertexCount, sourceVertexOffset);
}
function updateMeshUv2(engine, mesh, uvs2, vertexOffset = 0, vertexCount, sourceVertexOffset = 0) {
  const gpu = mesh._gpu;
  if (!gpu.uv2Buffer) {
    return;
  }
  writeVertexAttributeRange(engine, mesh, gpu.uv2Buffer, uvs2, 2, vertexOffset, vertexCount, sourceVertexOffset);
}
function updateMeshTangents(engine, mesh, tangents, vertexOffset = 0, vertexCount, sourceVertexOffset = 0) {
  const gpu = mesh._gpu;
  if (!gpu.tangentBuffer) {
    return;
  }
  writeVertexAttributeRange(engine, mesh, gpu.tangentBuffer, tangents, 4, vertexOffset, vertexCount, sourceVertexOffset);
}
function createSphere(engine, options) {
  const data = createSphereData(options);
  return createMeshFromData(engine, "sphere", data.positions, data.normals, data.indices, data.uvs);
}
function createBox(engine, options = 1) {
  const data = createBoxData(options);
  return createMeshFromData(engine, "box", data.positions, data.normals, data.indices, data.uvs);
}
function createTorus(engine, options) {
  const data = createTorusData(options);
  return createMeshFromData(engine, "torus", data.positions, data.normals, data.indices, data.uvs);
}
function createTorusKnot(engine, options) {
  const data = createTorusKnotData(options);
  return createMeshFromData(engine, "torusKnot", data.positions, data.normals, data.indices, data.uvs);
}
async function createGroundFromHeightMap(engine, url, options) {
  const data = await createGroundFromHeightMap$1(url, options);
  return createMeshFromData(engine, "ground", data.positions, data.normals, data.indices, data.uvs);
}
function createGround(engine, options) {
  const data = createFlatGroundData(options);
  return createMeshFromData(engine, "ground", data.positions, data.normals, data.indices, data.uvs);
}
function createCylinder(engine, options) {
  const data = createCylinderData(options);
  return createMeshFromData(engine, "cylinder", data.positions, data.normals, data.indices, data.uvs);
}
function createCapsule(engine, options) {
  const data = createCapsuleData(options);
  return createMeshFromData(engine, "capsule", data.positions, data.normals, data.indices, data.uvs);
}
function createPlane(engine, options) {
  const data = createPlaneData(options);
  return createMeshFromData(engine, "plane", data.positions, data.normals, data.indices, data.uvs);
}
function createDisc(engine, options) {
  const data = createDiscData(options);
  return createMeshFromData(engine, "disc", data.positions, data.normals, data.indices, data.uvs);
}
function createPolyhedron(engine, options) {
  const data = createPolyhedronData(options);
  return createMeshFromData(engine, "polyhedron", data.positions, data.normals, data.indices, data.uvs);
}
function createRibbon(engine, options) {
  const data = createRibbonData(options);
  return createMeshFromData(engine, "ribbon", data.positions, data.normals, data.indices, data.uvs);
}
function createTube(engine, options) {
  const data = createTubeData(options);
  return createMeshFromData(engine, "tube", data.positions, data.normals, data.indices, data.uvs);
}
function createExtrudeShape(engine, options) {
  const data = createExtrudeShapeData(options);
  return createMeshFromData(engine, "extrude", data.positions, data.normals, data.indices, data.uvs);
}

export { createBox, createCapsule, createCylinder, createDisc, createExtrudeShape, createGround, createGroundFromHeightMap, createMeshFromData, createPlane, createPolyhedron, createRibbon, createSphere, createTorus, createTorusKnot, createTube, invalidateRenderBundles, resizeMeshGeometry, updateMeshColors, updateMeshGeometry, updateMeshGeometryCapacity, updateMeshNormals, updateMeshPositions, updateMeshTangents, updateMeshUv2, updateMeshUvs };
//# sourceMappingURL=mesh-factories.js.map
